const TOKEN_STORAGE_KEY = 'cenein_token';
const API_BASE_URL = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');

function obtenerApiBaseUrl() {
  if (
    typeof window !== 'undefined' &&
    window.APP_CONFIG &&
    Object.prototype.hasOwnProperty.call(window.APP_CONFIG, 'API_URL')
  ) {
    const runtimeUrl = String(window.APP_CONFIG.API_URL || '').trim().replace(/\/+$/, '');
    // La configuración de despliegue tiene prioridad; si está vacía, usar
    // REACT_APP_API_URL o el mismo origen (Docker/Caddy) como último recurso.
    if (runtimeUrl) return runtimeUrl;
  }
  return API_BASE_URL;
}

export function obtenerToken() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    return String(window.localStorage.getItem(TOKEN_STORAGE_KEY) || '');
  } catch (err) {
    return '';
  }
}

export function guardarToken(token) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const limpio = String(token || '').trim();
    if (!limpio) {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(TOKEN_STORAGE_KEY, limpio);
  } catch (err) {}
}

export function limpiarToken() {
  guardarToken('');
}

export async function iniciarSesionApi({ username, password }) {
  const payload = {
    username: String(username || '').trim(),
    password: String(password || '').trim(),
  };
  limpiarToken();
  const data = await fetchJsonApi('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  // Guardar token en localStorage para que funcione en Safari/iOS donde la cookie
  // puede no persistir (ITP, modo privado, PWA, etc.). Las peticiones usan Authorization.
  const token = data && (data.token ?? data.accessToken ?? data.access_token);
  if (token) guardarToken(token);
  return data;
}

export async function cerrarSesionApi() {
  try {
    await fetchJsonApi('/api/auth/logout', {
      method: 'POST',
    });
  } catch (err) {
    // Si la sesion ya expiro, seguimos limpiando estado local.
  } finally {
    limpiarToken();
  }
}

export async function obtenerUsuarioActualApi(opciones = {}) {
  const headers = new Headers(opciones.headers || {});
  if (!headers.has('Cache-Control')) headers.set('Cache-Control', 'no-cache');
  if (!headers.has('Pragma')) headers.set('Pragma', 'no-cache');
  try {
    const data = await fetchJsonApi('/api/auth/me', {
      ...opciones,
      headers,
    });
    return data;
  } catch (err) {
    return null;
  }
}

export function resolverApiUrl(path) {
  if (typeof path !== 'string' || !path.startsWith('/api/')) return null;
  const apiBaseUrl = obtenerApiBaseUrl();
  if (apiBaseUrl) return `${apiBaseUrl}${path}`;
  return null;
}

export function cambiarEstadoOperativoApi(id, newStateName, reason = '') {
  return fetchJsonApi(`/api/patients/${encodeURIComponent(id)}/state`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newStateName, reason }),
  });
}

export function obtenerHistorialEstadosApi(id) {
  return fetchJsonApi(`/api/patients/${encodeURIComponent(id)}/state-history`);
}

async function fetchJsonApi(path, opciones = {}) {
  const apiUrl = resolverApiUrl(path);
  const urls = apiUrl ? [apiUrl] : [path];

  const headers = new Headers(opciones.headers || {});
  const token = obtenerToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let lastError = null;
  for (const url of urls) {
    try {
      const respuesta = await fetch(url, {
        ...opciones,
        headers,
        credentials: 'include',
        cache: 'no-store',
      });
      if (!respuesta.ok) {
        let detalle = '';
        let errorData = null;
        try {
          const data = await respuesta.json();
          errorData = data;
          if (data && data.error) detalle = String(data.error);
        } catch (err) {}
        const error = new Error(detalle || `HTTP ${respuesta.status}`);
        error.status = respuesta.status;
        if (errorData && typeof errorData === 'object') {
          if (errorData.retryAfterSeconds != null) {
            error.retryAfterSeconds = Number(errorData.retryAfterSeconds);
          }
          error.data = errorData;
        }
        if (respuesta.status === 401) {
          throw error;
        }
        throw error;
      }
      return await respuesta.json();
    } catch (err) {
      lastError = err;
      if (opciones && opciones.signal && opciones.signal.aborted) {
        throw err;
      }
    }
  }
  throw lastError || new Error('No se pudo conectar a la API.');
}

async function fetchBlobApi(path, opciones = {}) {
  const apiUrl = resolverApiUrl(path);
  const urls = apiUrl ? [apiUrl] : [path];

  const headers = new Headers(opciones.headers || {});
  const token = obtenerToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let lastError = null;
  for (const url of urls) {
    try {
      const respuesta = await fetch(url, {
        ...opciones,
        headers,
        credentials: 'include',
        cache: 'no-store',
      });
      if (!respuesta.ok) {
        let detalle = '';
        try {
          const data = await respuesta.json();
          if (data && data.error) detalle = `: ${data.error}`;
        } catch (err) {}
        throw new Error(`HTTP ${respuesta.status}${detalle}`);
      }
      return await respuesta.blob();
    } catch (err) {
      lastError = err;
      if (opciones && opciones.signal && opciones.signal.aborted) throw err;
    }
  }
  throw lastError || new Error('No se pudo conectar a la API.');
}

export async function obtenerPacientes(opciones = {}) {
  try {
    const data = await fetchJsonApi('/api/patients', opciones);
    return data;
  } catch (err) {
    throw new Error('No se pudieron cargar los pacientes.');
  }
}

export async function obtenerPacientePorId(id, opciones = {}) {
  try {
    const data = await fetchJsonApi(`/api/patients/${id}`, opciones);
    return data;
  } catch (err) {
    return null;
  }
}

export async function crearPacienteApi(paciente) {
  try {
    const data = await fetchJsonApi('/api/patients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(paciente),
    });
    return data;
  } catch (err) {
    throw new Error(err.message || 'No se pudo guardar el paciente.');
  }
}

export const obtenerExpedientesPacientesApi = () => fetchJsonApi('/api/expedientes');
export const eliminarDocumentoExpedienteApi = (registro, campo) => {
  const admissionId = registro.expediente?.[`${campo}AdmissionId`] || (registro.admissionOnly ? registro.id : null);
  const ruta = admissionId
    ? `/api/admisiones/${encodeURIComponent(admissionId)}/expediente/${encodeURIComponent(campo)}`
    : `/api/expedientes/${encodeURIComponent(registro.patientId)}/${encodeURIComponent(campo)}`;
  return fetchJsonApi(ruta, { method: 'DELETE' });
};
export const guardarDocumentoPacienteApi = (id, campo, file) => {
  const body = new FormData();
  body.append('documento', file);
  return fetchJsonApi(`/api/expedientes/${encodeURIComponent(id)}/${encodeURIComponent(campo)}`, { method: 'POST', body });
};
export const descargarDocumentoPacienteApi = (id, campo) =>
  fetchBlobApi(`/api/expedientes/${encodeURIComponent(id)}/${encodeURIComponent(campo)}/archivo`);

export async function actualizarPacienteApi(paciente) {
  return crearPacienteApi(paciente);
}

export async function eliminarPacienteApi(pacienteId) {
  try {
    const data = await fetchJsonApi(`/api/patients/${pacienteId}`, {
      method: 'DELETE',
    });
    return data;
  } catch (err) {
    throw new Error(String(err?.message || 'No se pudo eliminar el paciente.'));
  }
}

export async function cambiarEstadoPacienteApi(pacienteId, activo, fechas = {}) {
  try {
    const data = await fetchJsonApi(`/api/patients/${pacienteId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ activo: Boolean(activo), ...fechas }),
    });
    return data;
  } catch (err) {
    throw new Error(String(err?.message || 'No se pudo actualizar el estado del paciente.'));
  }
}

export async function cambiarBajaPacienteApi(pacienteId, dadoDeBaja) {
  try {
    const data = await fetchJsonApi(`/api/patients/${pacienteId}/discharge`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dadoDeBaja: Boolean(dadoDeBaja) }),
    });
    return data;
  } catch (err) {
    throw new Error(String(err?.message || 'No se pudo actualizar la baja del paciente.'));
  }
}

export async function crearSolicitudPacienteApi(pacienteId, payload = {}) {
  try {
    const data = await fetchJsonApi(`/api/patients/${pacienteId}/requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return data;
  } catch (err) {
    throw new Error(String(err?.message || 'No se pudo guardar la solicitud.'));
  }
}

export async function actualizarEstadoSolicitudPacienteApi(
  pacienteId,
  solicitudId,
  estadoAutorizacion
) {
  try {
    return await fetchJsonApi(
      `/api/patients/${pacienteId}/requests/${solicitudId}/status`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estadoAutorizacion }),
      }
    );
  } catch (err) {
    throw new Error(String(err?.message || 'No se pudo actualizar la autorización.'));
  }
}

export async function actualizarFechasSolicitudPacienteApi(
  pacienteId,
  solicitudId,
  fechaInicio,
  fechaFin
) {
  try {
    return await fetchJsonApi(
      `/api/patients/${pacienteId}/requests/${solicitudId}/dates`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fechaInicio, fechaFin }),
      }
    );
  } catch (err) {
    throw new Error(String(err?.message || 'No se pudieron actualizar las fechas de la solicitud.'));
  }
}

export async function eliminarSolicitudPacienteApi(pacienteId, solicitudId) {
  try {
    return await fetchJsonApi(
      `/api/patients/${pacienteId}/requests/${solicitudId}`,
      { method: 'DELETE' }
    );
  } catch (err) {
    throw new Error(String(err?.message || 'No se pudo eliminar la solicitud.'));
  }
}

export async function actualizarHorariosSolicitudPacienteApi(
  pacienteId,
  solicitudId,
  horarios
) {
  try {
    return await fetchJsonApi(
      `/api/patients/${pacienteId}/requests/${solicitudId}/turns`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ horarios: Array.isArray(horarios) ? horarios : [] }),
      }
    );
  } catch (err) {
    throw new Error(String(err?.message || 'No se pudieron actualizar los horarios de la solicitud.'));
  }
}

export async function actualizarCronogramaPacienteApi(pacienteId, horarios) {
  try {
    return await fetchJsonApi(`/api/patients/${pacienteId}/turns`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ horarios: Array.isArray(horarios) ? horarios : [] }),
    });
  } catch (err) {
    throw new Error(String(err?.message || 'No se pudo actualizar el cronograma vigente.'));
  }
}

export async function agregarTratamientosApi(pacienteId, tratamientos) {
  try {
    const data = await fetchJsonApi(`/api/patients/${pacienteId}/treatments`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tratamientos }),
    });
    return data;
  } catch (err) {
    throw new Error('No se pudieron agregar tratamientos.');
  }
}

export async function eliminarTratamientoApi(pacienteId, tratamiento) {
  try {
    const data = await fetchJsonApi(`/api/patients/${pacienteId}/treatments`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tratamiento }),
    });
    return data;
  } catch (err) {
    throw new Error('No se pudo eliminar el tratamiento.');
  }
}

export async function alternarTurnoApi(pacienteId, tratamiento, clave, mes) {
  try {
    const data = await fetchJsonApi(`/api/patients/${pacienteId}/turns/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tratamiento, clave, mes }),
    });
    return data;
  } catch (err) {
    throw new Error('No se pudo actualizar el turno.');
  }
}

export async function guardarAsistenciaApi(pacienteId, asistencia) {
  try {
    const data = await fetchJsonApi(`/api/patients/${pacienteId}/attendances`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(asistencia),
    });
    return data;
  } catch (err) {
    throw new Error('No se pudo guardar la asistencia.');
  }
}

export async function guardarTurnoExcepcionApi(pacienteId, tratamiento, fecha, hora, activo) {
  try {
    const data = await fetchJsonApi(`/api/patients/${pacienteId}/turns/override`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tratamiento, fecha, hora, activo }),
    });
    return data;
  } catch (err) {
    throw new Error('No se pudo guardar la excepcion.');
  }
}

export async function moverTurnosHorarioApi({
  fromDay,
  toDay,
  patientId,
  month,
}) {
  try {
    const data = await fetchJsonApi('/api/patients/turns/shift', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fromDay, toDay, patientId, month }),
    });
    return data;
  } catch (err) {
    throw new Error('No se pudo mover el horario.');
  }
}

export async function moverTurnosFechaApi({
  fromDate,
  toDate,
  patientId,
  tratamiento,
  obraSocialId,
  month,
  year,
  onlyDisable,
}) {
  try {
    const data = await fetchJsonApi('/api/patients/turns/shift-date', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fromDate,
        toDate,
        patientId,
        tratamiento,
        obraSocialId,
        month,
        year,
        onlyDisable: Boolean(onlyDisable),
      }),
    });
    return data;
  } catch (err) {
    throw new Error(
      String(err?.message || 'No se pudo mover el turno por fecha.')
    );
  }
}

export async function revertirTurnosFechaApi({
  operationId,
}) {
  try {
    const data = await fetchJsonApi('/api/patients/turns/revert-date-shift', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        operationId,
      }),
    });
    return data;
  } catch (err) {
    throw new Error(
      String(err?.message || 'No se pudo revertir el turno por fecha.')
    );
  }
}

export async function obtenerUltimoCambioFechaApi(opciones = {}) {
  try {
    const data = await fetchJsonApi(
      '/api/patients/turns/last-date-shift',
      opciones
    );
    return data?.operation || null;
  } catch (err) {
    throw new Error(
      String(err?.message || 'No se pudo obtener el ultimo cambio de fecha.')
    );
  }
}

function normalizarObrasSociales(lista) {
  if (!Array.isArray(lista)) return [];
  const map = new Map();
  for (const item of lista) {
    let id = '';
    let label = '';
    if (typeof item === 'string' || typeof item === 'number') {
      id = String(item).trim();
      label = id;
    } else if (item && typeof item === 'object') {
      id = String(item.id || item.obraSocialId || item.value || '').trim();
      label = String(item.label || item.nombre || item.name || id).trim();
    }
    if (!id) continue;
    map.set(id, { id, label: label || id });
  }
  return Array.from(map.values()).sort((a, b) => a.id.localeCompare(b.id));
}

export async function obtenerObrasSocialesApi(opciones = {}) {
  try {
    const data = await fetchJsonApi('/api/obras-sociales', opciones);
    return normalizarObrasSociales(data);
  } catch (err) {
    throw new Error('No se pudieron cargar las obras sociales.');
  }
}

export async function descargarObraSocialPacientePdf({
  pacienteId,
  obraSocialId,
  tratamiento,
  mes,
  anio,
  signal,
}) {
  const qs = new URLSearchParams();
  if (tratamiento) qs.set('tratamiento', String(tratamiento));
  if (mes != null) qs.set('mes', String(mes));
  if (anio != null) qs.set('anio', String(anio));
  const path = `/api/patients/${encodeURIComponent(pacienteId)}/obras-sociales/${encodeURIComponent(
    obraSocialId
  )}?${qs.toString()}`;
  return fetchBlobApi(path, { method: 'GET', signal });
}

export async function exportarAsistenciasPdf({
  patientIds,
  tratamiento,
  mes,
  anio,
  obraSocialId,
  formato,
  signal,
}) {
  return fetchBlobApi('/api/attendances/export', {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      patientIds: Array.isArray(patientIds) ? patientIds : [],
      tratamiento,
      mes,
      anio,
      obraSocialId,
      formato,
    }),
  });
}

export async function obtenerInformesApi(opciones = {}) {
  const patientId = opciones.patientId
    ? `?patientId=${encodeURIComponent(opciones.patientId)}`
    : '';
  return fetchJsonApi(`/api/reports${patientId}`, opciones);
}

export async function obtenerInformeApi(id) {
  return fetchJsonApi(`/api/reports/${encodeURIComponent(id)}`);
}

export async function previsualizarInformeApi(data) {
  return fetchJsonApi('/api/reports/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function actualizarInformeApi(id, data) {
  return fetchJsonApi(`/api/reports/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function eliminarInformeApi(id) {
  return fetchJsonApi(`/api/reports/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function obtenerPlantillasInformesApi(opciones = {}) {
  return fetchJsonApi('/api/reports/templates', opciones);
}

export async function crearInformeApi(data) {
  return fetchJsonApi('/api/reports', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function crearPlantillaInformeApi(data) {
  return fetchJsonApi('/api/reports/templates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function descargarInformePdfApi(id, opciones = {}) {
  return fetchBlobApi(`/api/reports/${encodeURIComponent(id)}/pdf`, opciones);
}

export async function obtenerUsuariosApi(opciones = {}) {
  const data = await fetchJsonApi('/api/users', opciones);
  return Array.isArray(data) ? data : [];
}

export async function obtenerUsuariosBloqueadosApi(opciones = {}) {
  const data = await fetchJsonApi('/api/users/blocked', opciones);
  return Array.isArray(data) ? data : [];
}

export async function obtenerActividadUsuariosApi(opciones = {}) {
  const data = await fetchJsonApi('/api/users/activity', opciones);
  return Array.isArray(data) ? data : [];
}

export async function desbloquearUsuarioApi(username) {
  return fetchJsonApi(`/api/users/blocked/${encodeURIComponent(username)}/unlock`, {
    method: 'POST',
  });
}

export async function crearUsuarioApi({ username, password, isAdmin, modulePermissions }) {
  return fetchJsonApi('/api/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password, isAdmin: Boolean(isAdmin), modulePermissions }),
  });
}

export async function actualizarUsuarioApi(userId, payload = {}) {
  return fetchJsonApi(`/api/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function eliminarUsuarioApi(userId) {
  return fetchJsonApi(`/api/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
}

export async function cambiarPasswordActualApi({ currentPassword, newPassword }) {
  return fetchJsonApi('/api/auth/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export async function obtenerAdmisionesApi(opciones = {}) {
  const data = await fetchJsonApi('/api/admisiones', opciones);
  return Array.isArray(data) ? data : [];
}

export async function obtenerExpedientesAdmisionesApi(opciones = {}) {
  const data = await fetchJsonApi('/api/admisiones/expedientes', opciones);
  return Array.isArray(data) ? data : [];
}

export async function obtenerAdmisionApi(id, opciones = {}) {
  try {
    return await fetchJsonApi(`/api/admisiones/${encodeURIComponent(id)}`, opciones);
  } catch (err) {
    return null;
  }
}

export async function crearAdmisionApi(datos) {
  return fetchJsonApi('/api/admisiones', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(datos),
  });
}

export async function actualizarAdmisionApi(id, datos) {
  return fetchJsonApi(`/api/admisiones/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(datos),
  });
}

export async function eliminarAdmisionApi(id) {
  return fetchJsonApi(`/api/admisiones/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function obtenerRevisionAdmisionApi(admisionId, opciones = {}) {
  try {
    return await fetchJsonApi(
      `/api/admisiones/${encodeURIComponent(admisionId)}/revision`,
      opciones
    );
  } catch (err) {
    return null;
  }
}

export async function guardarRevisionAdmisionApi(admisionId, datos) {
  return fetchJsonApi(`/api/admisiones/${encodeURIComponent(admisionId)}/revision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(datos),
  });
}

export async function finalizarAdmisionApi(admisionId) {
  return fetchJsonApi(`/api/admisiones/${encodeURIComponent(admisionId)}/finalizar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function obtenerExpedienteAdmisionApi(admisionId, opciones = {}) {
  try {
    return await fetchJsonApi(
      `/api/admisiones/${encodeURIComponent(admisionId)}/expediente`,
      opciones
    );
  } catch (err) {
    return null;
  }
}

export async function guardarExpedienteAdmisionApi(admisionId, formData) {
  const path = `/api/admisiones/${encodeURIComponent(admisionId)}/expediente`;
  const apiUrl = resolverApiUrl(path);
  const urls = apiUrl ? [apiUrl] : [path];
  const headers = new Headers();
  const token = obtenerToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let lastError = null;
  for (const url of urls) {
    try {
      const respuesta = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });
      if (!respuesta.ok) {
        let detalle = '';
        try {
          const data = await respuesta.json();
          if (data?.error) detalle = data.error;
        } catch (err) {}
        throw new Error(detalle || `HTTP ${respuesta.status}`);
      }
      return respuesta.json();
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('No se pudo conectar a la API.');
}

export function urlArchivoExpediente(admisionId, campo) {
  const path = `/api/admisiones/${encodeURIComponent(admisionId)}/expediente/${encodeURIComponent(
    campo
  )}/archivo`;
  return resolverApiUrl(path) || path;
}

export async function obtenerInfoArchivoExpedienteApi(admisionId, campo) {
  try {
    return await fetchJsonApi(
      `/api/admisiones/${encodeURIComponent(admisionId)}/expediente/${encodeURIComponent(
        campo
      )}/info`
    );
  } catch (err) {
    return null;
  }
}

export async function descargarArchivoExpedienteApi(admisionId, campo) {
  return fetchBlobApi(
    `/api/admisiones/${encodeURIComponent(admisionId)}/expediente/${encodeURIComponent(
      campo
    )}/archivo`,
    { method: 'GET' }
  );
}

export function soporteApi(path = '', opciones = {}) {
  return fetchJsonApi(`/api/support${path}`, {
    ...opciones,
    ...(opciones.body ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opciones.body) } : {}),
  });
}

// Agendas: centros, consultorios y turnos fijos semanales.
const jsonPut = (method, body) => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
export const obtenerCatalogoProfesionalesApi = () => fetchJsonApi('/api/agendas/profesionales/catalogo');
export const crearProfesionalAgendaApi = (nombre) => fetchJsonApi('/api/agendas/profesionales/catalogo', jsonPut('POST', { nombre }));
export const renombrarProfesionalAgendaApi = (id, nombre) => fetchJsonApi(`/api/agendas/profesionales/catalogo/${encodeURIComponent(id)}`, jsonPut('PATCH', { nombre }));
export const eliminarProfesionalAgendaApi = (id, { forzar = false } = {}) =>
  fetchJsonApi(`/api/agendas/profesionales/catalogo/${encodeURIComponent(id)}${forzar ? '?forzar=1' : ''}`, { method: 'DELETE' });
export const obtenerSedesAgendaApi = () => fetchJsonApi('/api/agendas/sedes');
// Solapa Profesionales: datos de cada profesional (solo administradores).
export const obtenerProfesionalesApi = () => fetchJsonApi('/api/profesionales');
export const crearProfesionalApi = (datos) => fetchJsonApi('/api/profesionales', jsonPut('POST', datos));
export const guardarDatosProfesionalApi = (id, datos) => fetchJsonApi(`/api/profesionales/${encodeURIComponent(id)}`, jsonPut('PATCH', datos));
// Equipo Cenein: centro → área → profesionales, con el consultorio de cada día.
export const obtenerEquipoApi = () => fetchJsonApi('/api/agendas/equipo');
export const agregarMiembroEquipoApi = (datos) => fetchJsonApi('/api/agendas/equipo/miembros', jsonPut('POST', datos));
export const quitarMiembroEquipoApi = (id) => fetchJsonApi(`/api/agendas/equipo/miembros/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const guardarConsultoriosEquipoApi = (id, consultorios) =>
  fetchJsonApi(`/api/agendas/equipo/miembros/${encodeURIComponent(id)}/consultorios`, jsonPut('PUT', { consultorios }));
export const precargarEquipoApi = (sedeId) => fetchJsonApi('/api/agendas/equipo/precargar', jsonPut('POST', { sedeId }));
export const obtenerAgendaSemanaApi = (sedeId) => fetchJsonApi(`/api/agendas/semana?sede=${encodeURIComponent(sedeId)}`);
export const obtenerAgendaDiaApi = (sedeId, fecha) =>
  fetchJsonApi(`/api/agendas/dia?sede=${encodeURIComponent(sedeId)}&fecha=${encodeURIComponent(fecha)}`);
export const guardarAsistenciasAgendaApi = (sedeId, fecha, registros) =>
  fetchJsonApi('/api/agendas/asistencias', jsonPut('PUT', { sedeId, fecha, registros }));
export const guardarTurnoAgendaApi = (id, datos) => fetchJsonApi(`/api/agendas/turnos/${encodeURIComponent(id)}`, jsonPut('PUT', datos));
export const restaurarTurnosAgendaApi = (turnos) => fetchJsonApi('/api/agendas/restaurar-turnos', jsonPut('PUT', { turnos }));
export const guardarProfesionalAgendaApi = (consultorioId, datos) =>
  fetchJsonApi(`/api/agendas/consultorios/${encodeURIComponent(consultorioId)}/profesional`, jsonPut('PUT', datos));
export const obtenerSinAsociarAgendaApi = () => fetchJsonApi('/api/agendas/sin-asociar');
export const obtenerSinAutorizacionAgendaApi = (mes) => fetchJsonApi(`/api/agendas/sin-autorizacion?mes=${encodeURIComponent(mes)}`);
export const asociarPacienteAgendaApi = (ids, patientId) => fetchJsonApi('/api/agendas/asociar', jsonPut('POST', { ids, patientId }));
export const crearConsultorioApi = (sedeId, nombre) => fetchJsonApi('/api/agendas/consultorios', jsonPut('POST', { sedeId, nombre }));
export const renombrarConsultorioApi = (id, nombre) =>
  fetchJsonApi(`/api/agendas/consultorios/${encodeURIComponent(id)}`, jsonPut('PATCH', { nombre }));
export const eliminarConsultorioApi = (id) => fetchJsonApi(`/api/agendas/consultorios/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const importarAgendaApi = (csv, { confirmar = false, reemplazar = false } = {}) => {
  const params = new URLSearchParams();
  if (confirmar) params.set('confirmar', '1');
  if (reemplazar) params.set('reemplazar', '1');
  return fetchJsonApi(`/api/agendas/importar?${params}`, {
    method: 'POST',
    headers: { 'Content-Type': 'text/csv' },
    body: csv,
  });
};
export const obtenerPlanillasProfesionalesApi = (mes = '') =>
  fetchJsonApi(`/api/agendas/profesionales${mes ? `?mes=${encodeURIComponent(mes)}` : ''}`);
export const obtenerPlanillaDobleEntradaApi = (mes = '') =>
  fetchJsonApi(`/api/agendas/profesionales/planilla${mes ? `?mes=${encodeURIComponent(mes)}` : ''}`);
export const contarPlanillasPendientesApi = () => fetchJsonApi('/api/agendas/profesionales/pendientes');
export const obtenerPlanillaProfesionalApi = (id) => fetchJsonApi(`/api/agendas/profesionales/${encodeURIComponent(id)}`);
export const importarFormularioProfesionalesApi = (csv, { confirmar = false } = {}) =>
  fetchJsonApi(`/api/agendas/profesionales/importar${confirmar ? '?confirmar=1' : ''}`, {
    method: 'POST',
    headers: { 'Content-Type': 'text/csv' },
    body: csv,
  });
export const obtenerScriptFormularioProfesionalesApi = () => fetchJsonApi('/api/agendas/profesionales/formulario-script');
export const obtenerEnviosFormularioApi = () => fetchJsonApi('/api/agendas/profesionales/envios');
export const guardarEmailsProfesionalesApi = (contactos) =>
  fetchJsonApi('/api/agendas/profesionales/contactos', jsonPut('PUT', { contactos }));
export const cancelarEnvioFormulariosApi = (id) =>
  fetchJsonApi(`/api/agendas/profesionales/envios/${encodeURIComponent(id)}/cancelar`, jsonPut('POST', {}));
export const pedirEnvioFormulariosApi = (profesionales = null, mes = null) =>
  fetchJsonApi('/api/agendas/profesionales/envios', jsonPut('POST', { ...(profesionales ? { profesionales } : {}), ...(mes ? { mes } : {}) }));
export const aprobarPlanillaProfesionalApi = (id, asignaciones = {}) =>
  fetchJsonApi(`/api/agendas/profesionales/${encodeURIComponent(id)}/aprobar`, jsonPut('POST', { asignaciones }));
export const editarPlanillaProfesionalApi = (id, turnos) =>
  fetchJsonApi(`/api/agendas/profesionales/${encodeURIComponent(id)}/turnos`, jsonPut('PUT', { turnos }));
export const obtenerValoresAgendaApi = (mes) =>
  fetchJsonApi(`/api/agendas/profesionales/valores?mes=${encodeURIComponent(mes)}`);
export const guardarValoresAgendaApi = (datos) =>
  fetchJsonApi('/api/agendas/profesionales/valores', jsonPut('PUT', datos));
export const cambiarDisciplinaPlanillaApi = (id, disciplina) =>
  fetchJsonApi(`/api/agendas/profesionales/${encodeURIComponent(id)}/disciplina`, jsonPut('PUT', { disciplina }));
export const eliminarPlanillaProfesionalApi = (id) =>
  fetchJsonApi(`/api/agendas/profesionales/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const rechazarPlanillaProfesionalApi = (id, motivo) =>
  fetchJsonApi(`/api/agendas/profesionales/${encodeURIComponent(id)}/rechazar`, jsonPut('POST', { motivo }));
